import { useState, useEffect } from 'react'
import { api, RiskAppetite as RiskAppetiteType, ToleranceBreach } from '../lib/api'
import { useToast } from '../components/Toast'
import {
  Target,
  Sparkles,
  AlertTriangle,
  AlertCircle,
  Edit2,
  X,
  Save,
  History,
  RefreshCw,
} from 'lucide-react'

const statusConfig: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'badge-red' },
  mitigating: { label: 'Mitigating', color: 'badge-yellow' },
  mitigated: { label: 'Mitigated', color: 'badge-green' },
  accepted: { label: 'Accepted', color: 'badge-blue' },
  closed: { label: 'Closed', color: 'badge-gray' },
}

export default function RiskAppetite() {
  const toast = useToast()

  // Risk Appetite state
  const [riskAppetites, setRiskAppetites] = useState<RiskAppetiteType[]>([])
  const [appetiteLevels, setAppetiteLevels] = useState<Record<number, { label: string; description: string; color: string }>>({})
  const [toleranceBreaches, setToleranceBreaches] = useState<ToleranceBreach[]>([])
  const [breachSummary, setBreachSummary] = useState<{
    total_breaches: number
    by_category: Record<string, number>
    critical_breaches: number
    above_tolerance: number
    above_max: number
  } | null>(null)
  const [loadingAppetite, setLoadingAppetite] = useState(false)
  const [savingAppetite, setSavingAppetite] = useState(false)
  const [seedingAppetite, setSeedingAppetite] = useState(false)
  const [editingAppetite, setEditingAppetite] = useState<RiskAppetiteType | null>(null)
  const [appetiteFormData, setAppetiteFormData] = useState({
    appetite_level: 3,
    tolerance_threshold: 12,
    max_acceptable_score: 16,
    description: '',
    appetite_statement: '',
  })
  const [showAppetiteEditModal, setShowAppetiteEditModal] = useState(false)

  // Load data on mount
  useEffect(() => {
    fetchRiskAppetite()
    fetchToleranceBreaches()
  }, [])

  // Fetch risk appetite settings
  async function fetchRiskAppetite() {
    setLoadingAppetite(true)
    try {
      const res = await api.getRiskAppetite()
      if (res.success) {
        setRiskAppetites(res.appetites)
        setAppetiteLevels(res.appetite_levels)
      }
    } catch (err) {
      console.error('Failed to load risk appetite:', err)
      setRiskAppetites([])
    } finally {
      setLoadingAppetite(false)
    }
  }

  // Fetch tolerance breaches
  async function fetchToleranceBreaches() {
    try {
      const res = await api.getToleranceBreaches()
      if (res.success) {
        setToleranceBreaches(res.breaches)
        setBreachSummary(res.summary)
      }
    } catch (err) {
      console.error('Failed to load tolerance breaches:', err)
      setToleranceBreaches([])
      setBreachSummary(null)
    }
  }

  // Seed default risk appetite
  async function seedRiskAppetite() {
    setSeedingAppetite(true)
    try {
      const res = await api.seedRiskAppetite()
      if (res.success) {
        toast.success(res.message)
        fetchRiskAppetite()
        fetchToleranceBreaches()
      } else {
        toast.error('Failed to load default appetite settings')
      }
    } catch (err: any) {
      console.error('Failed to seed risk appetite:', err)
      toast.error(err?.message || 'Failed to seed risk appetite defaults')
    } finally {
      setSeedingAppetite(false)
    }
  }

  // Save risk appetite changes
  async function handleSaveAppetite() {
    if (!editingAppetite) return
    setSavingAppetite(true)
    try {
      const res = await api.updateRiskAppetite({
        appetites: [{
          category: editingAppetite.category,
          appetite_level: appetiteFormData.appetite_level,
          tolerance_threshold: appetiteFormData.tolerance_threshold,
          max_acceptable_score: appetiteFormData.max_acceptable_score,
          description: appetiteFormData.description || undefined,
          appetite_statement: appetiteFormData.appetite_statement || undefined,
        }],
        change_reason: 'Updated via Risk Appetite page',
      })
      if (res.success) {
        toast.success(`Updated ${editingAppetite.display_name || editingAppetite.category} appetite settings`)
        setShowAppetiteEditModal(false)
        setEditingAppetite(null)
        fetchRiskAppetite()
        fetchToleranceBreaches()
      }
    } catch (err) {
      console.error('Failed to save risk appetite:', err)
      toast.error('Failed to save risk appetite settings')
    } finally {
      setSavingAppetite(false)
    }
  }

  // Open appetite edit modal
  function openAppetiteEditModal(appetite: RiskAppetiteType) {
    setEditingAppetite(appetite)
    setAppetiteFormData({
      appetite_level: appetite.appetite_level,
      tolerance_threshold: appetite.tolerance_threshold,
      max_acceptable_score: appetite.max_acceptable_score,
      description: appetite.description || '',
      appetite_statement: appetite.appetite_statement || '',
    })
    setShowAppetiteEditModal(true)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="h-6 w-6 text-primary-600" />
            Risk Appetite
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Define your organization's risk appetite and tolerance thresholds for each category
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              fetchRiskAppetite()
              fetchToleranceBreaches()
            }}
            className="btn-secondary"
            title="Refresh data"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {riskAppetites.length === 0 && !loadingAppetite && (
            <button
              onClick={seedRiskAppetite}
              disabled={seedingAppetite}
              className="btn-primary"
            >
              {seedingAppetite ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Loading...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Load Defaults
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Breach Summary Alert */}
      {breachSummary && breachSummary.total_breaches > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Tolerance Breaches Detected</h3>
              <p className="text-sm text-red-700 mt-1">
                {breachSummary.total_breaches} risk{breachSummary.total_breaches !== 1 ? 's' : ''} exceed tolerance thresholds
                {breachSummary.critical_breaches > 0 && (
                  <span className="font-semibold">
                    {' '}({breachSummary.critical_breaches} critical)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Appetite Cards */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Appetite Configuration by Category
          </h2>
          <div className="text-sm text-gray-500">
            {riskAppetites.length} categories configured
          </div>
        </div>

        {loadingAppetite ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : riskAppetites.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No risk appetite settings configured</p>
            <p className="text-sm text-gray-400 mt-1">Click "Load Defaults" to get started with standard categories</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {riskAppetites.map((appetite) => {
              const categoryBreaches = toleranceBreaches.filter(
                (b) => b.category?.toLowerCase() === appetite.category.toLowerCase() ||
                       b.appetite_category_name?.toLowerCase() === appetite.category.toLowerCase()
              )
              const hasCritical = categoryBreaches.some(b => b.breach_level === 'critical')

              return (
                <div
                  key={appetite.id}
                  className={`p-4 border rounded-lg ${
                    hasCritical ? 'border-red-300 bg-red-50' :
                    categoryBreaches.length > 0 ? 'border-orange-300 bg-orange-50' :
                    'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 capitalize">
                      {appetite.display_name || appetite.category}
                    </h3>
                    <button
                      onClick={() => openAppetiteEditModal(appetite)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit appetite settings"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Appetite Level Meter */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Appetite Level</span>
                      <span
                        className="font-medium px-2 py-0.5 rounded text-white"
                        style={{ backgroundColor: appetite.appetite_level_color || '#6B7280' }}
                      >
                        {appetite.appetite_level_label}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all duration-300"
                        style={{
                          width: `${(appetite.appetite_level / 5) * 100}%`,
                          backgroundColor: appetite.appetite_level_color || '#6B7280',
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>Risk Averse</span>
                      <span>Risk Seeking</span>
                    </div>
                  </div>

                  {/* Thresholds */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tolerance Threshold:</span>
                      <span className="font-medium text-gray-900">{appetite.tolerance_threshold}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Max Acceptable:</span>
                      <span className="font-medium text-gray-900">{appetite.max_acceptable_score}</span>
                    </div>
                  </div>

                  {/* Breach indicator */}
                  {categoryBreaches.length > 0 && (
                    <div className={`mt-3 pt-3 border-t ${
                      hasCritical ? 'border-red-200' : 'border-orange-200'
                    }`}>
                      <div className={`flex items-center gap-2 text-sm ${
                        hasCritical ? 'text-red-700' : 'text-orange-700'
                      }`}>
                        <AlertCircle className="h-4 w-4" />
                        <span>
                          {categoryBreaches.length} risk{categoryBreaches.length !== 1 ? 's' : ''} exceeding tolerance
                        </span>
                      </div>
                    </div>
                  )}

                  {appetite.description && (
                    <p className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                      {appetite.description}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Tolerance Breaches List */}
      {toleranceBreaches.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Risks Exceeding Tolerance ({toleranceBreaches.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Threshold</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Breach Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {toleranceBreaches.map((breach) => (
                  <tr key={breach.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{breach.title}</div>
                      {breach.owner_username && (
                        <div className="text-xs text-gray-500">Owner: {breach.owner_username}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700 capitalize">
                        {breach.category_name || breach.category || breach.appetite_category_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (breach.residual_risk_score || breach.risk_score) >= 20 ? 'bg-red-100 text-red-800' :
                        (breach.residual_risk_score || breach.risk_score) >= 12 ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {breach.residual_risk_score || breach.risk_score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-500">
                      {breach.tolerance_threshold}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        breach.breach_level === 'critical' ? 'bg-red-100 text-red-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {breach.breach_level === 'critical' ? 'Critical' : 'Above Tolerance'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusConfig[breach.status]?.color || 'badge-gray'
                      }`}>
                        {statusConfig[breach.status]?.label || breach.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Risk Appetite Edit Modal */}
      {showAppetiteEditModal && editingAppetite && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Risk Appetite - {editingAppetite.display_name || editingAppetite.category}
                </h3>
                <button
                  onClick={() => {
                    setShowAppetiteEditModal(false)
                    setEditingAppetite(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Appetite Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appetite Level
                  </label>
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={appetiteFormData.appetite_level}
                      onChange={(e) => setAppetiteFormData(prev => ({
                        ...prev,
                        appetite_level: parseInt(e.target.value)
                      }))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Very Low</span>
                      <span>Low</span>
                      <span>Moderate</span>
                      <span>High</span>
                      <span>Very High</span>
                    </div>
                    <div className="text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        appetiteFormData.appetite_level === 1 ? 'bg-green-100 text-green-800' :
                        appetiteFormData.appetite_level === 2 ? 'bg-blue-100 text-blue-800' :
                        appetiteFormData.appetite_level === 3 ? 'bg-yellow-100 text-yellow-800' :
                        appetiteFormData.appetite_level === 4 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        Level {appetiteFormData.appetite_level}: {
                          appetiteFormData.appetite_level === 1 ? 'Very Low (Risk Averse)' :
                          appetiteFormData.appetite_level === 2 ? 'Low' :
                          appetiteFormData.appetite_level === 3 ? 'Moderate' :
                          appetiteFormData.appetite_level === 4 ? 'High' :
                          'Very High (Risk Seeking)'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tolerance Threshold */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tolerance Threshold (1-25)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Risks with scores above this value will be flagged for review
                  </p>
                  <input
                    type="number"
                    min="1"
                    max="25"
                    value={appetiteFormData.tolerance_threshold}
                    onChange={(e) => setAppetiteFormData(prev => ({
                      ...prev,
                      tolerance_threshold: Math.min(25, Math.max(1, parseInt(e.target.value) || 1))
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Max Acceptable Score */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Acceptable Score (1-25)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Risks above this score require immediate attention and escalation
                  </p>
                  <input
                    type="number"
                    min="1"
                    max="25"
                    value={appetiteFormData.max_acceptable_score}
                    onChange={(e) => setAppetiteFormData(prev => ({
                      ...prev,
                      max_acceptable_score: Math.min(25, Math.max(1, parseInt(e.target.value) || 1))
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Appetite Statement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appetite Statement (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Formal statement describing the organization's appetite for this category
                  </p>
                  <textarea
                    value={appetiteFormData.appetite_statement}
                    onChange={(e) => setAppetiteFormData(prev => ({
                      ...prev,
                      appetite_statement: e.target.value
                    }))}
                    rows={3}
                    placeholder="e.g., The organization accepts moderate levels of operational risk in pursuit of efficiency gains..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={appetiteFormData.description}
                    onChange={(e) => setAppetiteFormData(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                    rows={2}
                    placeholder="Additional notes about this risk appetite setting..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowAppetiteEditModal(false)
                    setEditingAppetite(null)
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAppetite}
                  disabled={savingAppetite}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingAppetite ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
